import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Stack } from '@mui/material';
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import type { Workout } from "../api/workout-service";
import WorkoutService from "../api/workout-service";

import ENV from "../env";
import FinishedBlock from "../components/workouts/FinishedBlock";
import WorkoutProcessBlock from "../components/workouts/WorkoutProcessBlock";
import WorkoutJoinBlock from "../components/workouts/WorkoutJoinBlock";
import FailureBlock from "../components/workouts/FailuredBlock";
import { rememberWorkout } from "../utils/last-workout";
import { QUIZ_INK, QUIZ_SURFACE } from "../components/workouts/quiz-theme";

let workoutUpdateInterval: ReturnType<typeof setInterval> | null = null;

export default function WorkoutPage() {
    const [ finished, setFinished ] = useState(false);
    const [ failed, setFailed ] = useState(false);
    const [ workout, setWorkout ] = useState<Workout | null>(null);
    const [ joined, setJoined ] = useState(false);

    const navigate = useNavigate();

    // Load workout data
    const { id } = useParams();

    useEffect(() => {
        if (!id) {
            console.log("Unable to receive workout id");
            navigate('/404');
            return;
        }

        setFinished(false);
        setFailed(false);
        setWorkout(null);
        setJoined(false);

        function loadWorkout(workoutId: string) {
            WorkoutService.getWorkout(workoutId)
                .then(data => {
                    if (!data.id) {
                        console.log("Unable to load workout by id");
                        if (workoutUpdateInterval) clearInterval(workoutUpdateInterval);
                        navigate('/404');
                        return;
                    }

                    console.log("Workout loaded: ", data);
                    setWorkout(data);

                    if (data.currentUser) {
                        setJoined(true);
                        rememberWorkout(data.id);
                    }
                })
                .catch(reason => {
                    console.log("Unable to load workout by id", reason);
                    navigate('/404');
                });
        }

        loadWorkout(id);
        if (workoutUpdateInterval) clearInterval(workoutUpdateInterval);
        workoutUpdateInterval = setInterval(() => loadWorkout(id), 5_000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ id ]);

    function answerSubmitAction(questionId: string,
                                answerId: number,
                                successCallback: () => void,
                                errorCallback: () => void) {

        if (!workout) return;

        WorkoutService.submitAnswer(workout.id, questionId, answerId)
            .then(result => {
                if (result) {
                    console.log(`Answer to question ${ questionId } submitted.`);
                    setWorkout(result);

                    successCallback();
                    return;
                }

                errorCallback();
            })
            .catch(reason => {
                console.error("Unable to submit answer: " + reason);
                errorCallback();
            });
    }

    function joinAction(callback: () => void) {
        if (!workout) return;

        WorkoutService.joinWorkout(workout.id)
            .then(result => {
                if (result) {
                    console.log("Workout joined");
                    setWorkout(result);
                    setJoined(true);

                    if (callback) callback();
                    return;
                }

                console.error("Unable to join workout");
            })
            .catch(reason => {
                console.error("Unable to join workout: " + reason);
            });
    }

    function finishAction() {
        if (!workout) return;
        if (workoutUpdateInterval) clearInterval(workoutUpdateInterval);

        WorkoutService.finishWorkout(workout.id)
            .then(result => {
                if (result) {
                    console.log("Workout finished.");
                    setFinished(true);
                }
            })
            .catch(reason => {
                console.error("Unable to finish workout: " + reason);
            })
    }

    function failedAction() {
        if (!workout) return;
        if (workoutUpdateInterval) clearInterval(workoutUpdateInterval);

        WorkoutService.finishWorkout(workout.id)
            .then(result => {
                if (result) {
                    console.log("Workout failed.");
                    setFailed(true);
                }
            })
            .catch(reason => {
                console.error("Unable to finish workout: " + reason);
            })
    }

    return <>
        <Helmet>
            <title>Workout | { ENV.APP_NAME }</title>
        </Helmet>

        { /* The workout is played full screen, away from the navigation */ }
        <Stack sx={ { minHeight: '100vh', color: QUIZ_INK, bgcolor: '#F4F6F8' } }>
            { !!workout && <>
                <WorkoutProcessBlock
                    workout={ workout }
                    answerSubmitAction={ answerSubmitAction }
                    finishAction={ finishAction }
                    failedAction={ failedAction }
                    show={ joined && !finished && !failed }/>

                { !joined && <QuizPanel><WorkoutJoinBlock workout={ workout } joinAction={ joinAction } show/></QuizPanel> }
                { failed && <QuizPanel><FailureBlock show/></QuizPanel> }
                { joined && finished && <QuizPanel><FinishedBlock show workout={ workout }/></QuizPanel> }
            </> }
        </Stack>
    </>;
}

/** The card the screens around the questions sit in: joining, and the end of a workout. */
function QuizPanel({ children }: { children: ReactNode }) {
    return (
        <Box sx={ { flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 6 } }>
            <Box sx={ { ...QUIZ_SURFACE, width: '100%', maxWidth: 760, p: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', gap: 2.5 } }>
                { children }
            </Box>
        </Box>
    );
}
